import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

// Durée de rafraîchissement anticipé (1 heure avant expiration)
const REFRESH_THRESHOLD_SECONDS = 3600;

// Parse JWT pour extraire le payload
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

// Vérifie si le token est valide et non expiré
const isTokenValid = (token) => {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return false;
  const now = Date.now() / 1000;
  return payload.exp > now;
};

// Vérifie si le token expire bientôt (pour rafraîchissement proactif)
const isTokenExpiringSoon = (token) => {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return false;
  const now = Date.now() / 1000;
  return (payload.exp - now) < REFRESH_THRESHOLD_SECONDS;
};

// Nettoie la session et redirige
const clearSessionAndRedirect = (reason = 'Session expirée') => {
  console.warn(`${reason} - redirection vers login`);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('hasSeenSplash');
  // Évite les redirections multiples
  if (!window.location.pathname.includes('/login')) {
    window.location.replace('/login');
  }
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Add token to requests - avec vérification PROACTIVE de l'expiration
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // Vérification proactive avant chaque requête
    if (token) {
      // Si le token est expiré, ne pas faire la requête
      if (!isTokenValid(token)) {
        clearSessionAndRedirect('Token expiré');
        return Promise.reject(new Error('Token expired'));
      }
      
      // Si le token expire bientôt, log un avertissement (pour futur refresh)
      if (isTokenExpiringSoon(token)) {
        console.info('Token expire bientôt - pensez à vous reconnecter');
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors - seulement comme filet de sécurité
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Vérifier si c'est une erreur réseau (pas de réponse du serveur)
    if (!error.response) {
      console.error('Erreur réseau:', error.message);
      return Promise.reject(error);
    }
    
    const is401 = error.response?.status === 401;
    const hasToken = localStorage.getItem('token');
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    
    // Si 401 et nous avons un token (expiré ou invalide)
    // Ne pas rediriger si c'est une tentative de login/signup
    if (is401 && hasToken && !isAuthEndpoint) {
      clearSessionAndRedirect('Session invalide (401)');
    }
    
    return Promise.reject(error);
  }
);

// Export des fonctions utilitaires pour les composants
export { isTokenValid, isTokenExpiringSoon, parseJwt };
export default api;
